import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Download, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { api } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { getApiBase } from '../../lib/api';

const monthNames = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

export default function AttendancePage() {
  const [searchParams] = useSearchParams();
  const empIdParam = searchParams.get('employee');
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(empIdParam || '');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [attendance, setAttendance] = useState(null);
  const [statusDate, setStatusDate] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    api.get(`/employees`).then(r => {
      setEmployees(r.data);
      if (r.data.length > 0) {
        setSelectedEmp(current => current || r.data[0].id);
      }
    });
  }, []);

  useEffect(() => {
    if (!selectedEmp) return;
    api.get(`/employees/${selectedEmp}/attendance?month=${month}&year=${year}`)
      .then(r => setAttendance(r.data)).catch(() => setAttendance(null));
  }, [selectedEmp, month, year]);

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1); };

  const exportReport = () => {
    window.open(`${getApiBase()}/api/employees/${selectedEmp}/report?month=${month}&year=${year}&export_format=excel`, '_blank');
  };

  const badgeEmployee = async (action) => {
    if (!selectedEmp) return;
    try {
      const endpoint = action === 'in'
        ? `/employees/badge/admin-in/${selectedEmp}`
        : `/employees/badge/admin-out-by-employee/${selectedEmp}`;
      await api.post(endpoint);
      toast.success(action === 'in' ? 'Arrivee pointee' : 'Depart pointe');
      const { data } = await api.get(`/employees/${selectedEmp}/attendance?month=${month}&year=${year}`);
      setAttendance(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de pointage');
    }
  };

  const refreshAttendance = async () => {
    if (!selectedEmp) return;
    const { data } = await api.get(`/employees/${selectedEmp}/attendance?month=${month}&year=${year}`);
    setAttendance(data);
  };

  const markAttendanceStatus = async (status) => {
    if (!selectedEmp || !statusDate) return;
    try {
      await api.post(`/employees/${selectedEmp}/attendance-status`, {
        date: statusDate,
        status,
      });
      toast.success(status === 'leave' ? 'Conge enregistre' : 'Vacances enregistrees');
      await refreshAttendance();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de statut');
    }
  };

  const clearAttendanceStatus = async () => {
    if (!selectedEmp || !statusDate) return;
    try {
      await api.delete(`/employees/${selectedEmp}/attendance-status?date=${statusDate}`);
      toast.success('Statut retire');
      await refreshAttendance();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Suppression impossible');
    }
  };

  const timelineRows = [
    ...(attendance?.badges || []).map((badge) => ({
      id: badge.id,
      date: badge.date,
      type: 'badge',
      clock_in: badge.clock_in,
      clock_out: badge.clock_out,
      hours_worked: badge.hours_worked,
    })),
    ...(attendance?.attendance_marks || []).map((mark) => ({
      id: mark.id,
      date: mark.date,
      type: mark.status,
      note: mark.note,
    })),
  ].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div data-testid="admin-attendance-page">
      <h1 className="font-heading text-2xl font-bold text-[#2A2A28] mb-6">Suivi de Presence</h1>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <select className="h-10 px-3 rounded-xl border border-[#EBE8E0] bg-white text-sm flex-1" value={selectedEmp} onChange={e => setSelectedEmp(e.target.value)} data-testid="attendance-employee-select">
          {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
        </select>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="rounded-lg" onClick={prevMonth} data-testid="prev-month"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm font-medium min-w-[140px] text-center">{monthNames[month - 1]} {year}</span>
          <Button size="sm" variant="outline" className="rounded-lg" onClick={nextMonth} data-testid="next-month"><ChevronRight className="w-4 h-4" /></Button>
        </div>
        <Button onClick={exportReport} variant="outline" className="rounded-xl border-[#5B6B3F] text-[#5B6B3F]" data-testid="export-report-btn">
          <Download className="w-4 h-4 mr-1" /> Exporter Excel
        </Button>
        <Button onClick={() => badgeEmployee('in')} variant="outline" className="rounded-xl border-[#5B6B3F] text-[#5B6B3F]" data-testid="admin-badge-in-btn">
          Pointer entrée
        </Button>
        <Button onClick={() => badgeEmployee('out')} variant="outline" className="rounded-xl border-[#8C3A3A] text-[#8C3A3A]" data-testid="admin-badge-out-btn">
          Pointer sortie
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-[#EBE8E0] p-4 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-[#2A2A28] mb-2">Date de statut</label>
            <input
              type="date"
              className="h-10 px-3 rounded-xl border border-[#EBE8E0] bg-white text-sm w-full"
              value={statusDate}
              onChange={(e) => setStatusDate(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => markAttendanceStatus('leave')} variant="outline" className="rounded-xl border-[#D4AF37] text-[#D4AF37]">
              Marquer conge
            </Button>
            <Button onClick={() => markAttendanceStatus('vacation')} variant="outline" className="rounded-xl border-[#5B6B3F] text-[#5B6B3F]">
              Marquer vacances
            </Button>
            <Button onClick={clearAttendanceStatus} variant="outline" className="rounded-xl border-[#8C3A3A] text-[#8C3A3A]">
              Retirer le statut
            </Button>
          </div>
        </div>
      </div>

      {attendance && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-[#EBE8E0] p-4">
              <p className="text-xs text-[#7A7A75]">Heures travaillees</p>
              <p className="font-heading text-2xl font-bold text-[#2A2A28]">{attendance.total_hours}h</p>
            </div>
            <div className="bg-white rounded-xl border border-[#EBE8E0] p-4">
              <p className="text-xs text-[#7A7A75]">Heures prevues</p>
              <p className="font-heading text-2xl font-bold text-[#2A2A28]">{attendance.expected_hours}h</p>
            </div>
            <div className="bg-white rounded-xl border border-[#EBE8E0] p-4">
              <p className="text-xs text-[#7A7A75]">Difference</p>
              <p className={`font-heading text-2xl font-bold ${attendance.difference >= 0 ? 'text-[#5B6B3F]' : 'text-[#8C3A3A]'}`}>{attendance.difference > 0 ? '+' : ''}{attendance.difference}h</p>
            </div>
            <div className="bg-white rounded-xl border border-[#EBE8E0] p-4">
              <p className="text-xs text-[#7A7A75]">Jours travailles</p>
              <p className="font-heading text-2xl font-bold text-[#2A2A28]">{attendance.days_worked}</p>
            </div>
            <div className="bg-white rounded-xl border border-[#EBE8E0] p-4">
              <p className="text-xs text-[#7A7A75]">Conges</p>
              <p className="font-heading text-2xl font-bold text-[#D4AF37]">{attendance.leave_days || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-[#EBE8E0] p-4">
              <p className="text-xs text-[#7A7A75]">Vacances</p>
              <p className="font-heading text-2xl font-bold text-[#5B6B3F]">{attendance.vacation_days || 0}</p>
            </div>
          </div>

          {attendance.anomalies?.length > 0 && (
            <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-xl p-4 mb-6">
              <h3 className="text-sm font-medium text-[#D4AF37] flex items-center gap-1 mb-2"><AlertTriangle className="w-4 h-4" /> Anomalies</h3>
              {attendance.anomalies.map((a, i) => <p key={i} className="text-sm text-[#7A7A75]">{a.date}: {a.message}</p>)}
            </div>
          )}

          <div className="bg-white rounded-xl border border-[#EBE8E0] overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-[#EBE8E0] bg-[#FAF8F5]">
                <th className="text-left p-3 font-medium text-[#7A7A75]">Date</th>
                <th className="text-left p-3 font-medium text-[#7A7A75]">Type</th>
                <th className="text-left p-3 font-medium text-[#7A7A75]">Arrivee / Statut</th>
                <th className="text-left p-3 font-medium text-[#7A7A75]">Depart / Note</th>
                <th className="text-left p-3 font-medium text-[#7A7A75]">Heures</th>
              </tr></thead>
              <tbody>
                {timelineRows.map(row => (
                  <tr key={row.id} className="border-b border-[#EBE8E0] last:border-0">
                    <td className="p-3">{row.date}</td>
                    <td className="p-3">
                      {row.type === 'badge' ? (
                        <Badge className="bg-[#1E2D3D]/10 text-[#1E2D3D] text-xs">Travail</Badge>
                      ) : row.type === 'leave' ? (
                        <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] text-xs">Conge</Badge>
                      ) : (
                        <Badge className="bg-[#5B6B3F]/10 text-[#5B6B3F] text-xs">Vacances</Badge>
                      )}
                    </td>
                    <td className="p-3">
                      {row.type === 'badge'
                        ? (row.clock_in ? new Date(row.clock_in).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '-')
                        : (row.type === 'leave' ? 'Conge' : 'Vacances')}
                    </td>
                    <td className="p-3">
                      {row.type === 'badge'
                        ? (row.clock_out ? new Date(row.clock_out).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] text-xs">Manquant</Badge>)
                        : (row.note || '-')}
                    </td>
                    <td className="p-3 font-medium">{row.type === 'badge' ? (row.hours_worked ? `${row.hours_worked}h` : '-') : '-'}</td>
                  </tr>
                ))}
                {timelineRows.length === 0 && (
                  <tr><td colSpan={5} className="p-6 text-center text-[#7A7A75]">Aucun pointage ou statut ce mois</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
